import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Download, X } from 'lucide-react';
import { getRawAssetUrl } from '../../Api';
import logoBranca from '../../assets/Logo_copasul_white.svg';
import logoVerde from '../../assets/Logo_copasul.svg';
import iconePdf from '../../assets/pdf_icon.svg';
import { ehClara } from '../../styles/contraste';
import { Cabecalho, Foto, preencher } from './comum';
import * as S from './style';

// "Outros materiais" mostra os primeiros da lista; o resto fica no acervo.
const VISIVEIS = 5;

// "Cor da capa" do Squidex. O campo já valida o formato; aqui é a segunda trava
// antes de o valor entrar no CSS.
const corValida = (cor) => (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(cor || '') ? cor : null);

// Com imagem de capa, só a imagem (ela já traz título e logo). Sem imagem, o
// livro com logo, título e selo, na "Cor da capa" ou no verde da Copasul.
const Capa = ({ doc, grande = false }) => {
  const cor = corValida(doc.cor);
  const clara = Boolean(cor && ehClara(cor));
  return (
    <S.Capa $grande={grande} $cor={cor} $clara={clara} aria-hidden="true">
      {doc.capa ? (
        <Foto className="capa" id={doc.capa} largura={grande ? 640 : 480} />
      ) : (
        <span>
          <img src={clara ? logoVerde : logoBranca} alt="" />
          <b>{doc.titulo}</b>
          {doc.selo && <i>{doc.selo}</i>}
        </span>
      )}
    </S.Capa>
  );
};

const linkDoArquivo = (doc) => ({
  href: getRawAssetUrl(doc.arquivo),
  target: '_blank',
  rel: 'noopener noreferrer',
});

const Acervo = ({ documentos, aoFechar, secao }) => {
  const fechar = useRef(null);

  // Diálogo de verdade: foco no fechar, Esc fecha, fundo não rola, e o foco
  // volta para quem abriu (o `aoFechar` cuida disso).
  useEffect(() => {
    fechar.current?.focus();
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const aoTeclar = (e) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.body.style.overflow = antes;
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aoFechar]);

  return (
    <S.Fundo onClick={aoFechar}>
      <S.Janela role="dialog" aria-modal="true" aria-labelledby="acervo-titulo" onClick={(e) => e.stopPropagation()}>
        <div>
          <div>
            {secao.acervoChapeu && <S.Chapeu>{secao.acervoChapeu}</S.Chapeu>}
            {secao.acervoTitulo && <S.H4 id="acervo-titulo">{secao.acervoTitulo}</S.H4>}
          </div>
          <button type="button" ref={fechar} onClick={aoFechar} aria-label={secao.fecharRotulo || undefined}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <ul>
          {documentos.map((doc) => (
            <li key={doc.id}>
              <a {...linkDoArquivo(doc)}>
                <img src={iconePdf} alt="" width={28} height={28} />
                <span>
                  <b>{doc.titulo}</b>
                  <small>{[doc.categoria, doc.selo].filter(Boolean).join(' · ')}</small>
                </span>
                <i aria-hidden="true">
                  <Download size={18} />
                </i>
              </a>
            </li>
          ))}
        </ul>
      </S.Janela>
    </S.Fundo>
  );
};

// Componente `documento-pdf` do Squidex -> o que a tela usa. PDF sem título
// ou sem arquivo fica de fora: não há o que baixar.
const lerPdf = (item, chave) =>
  item && {
    id: chave,
    titulo: item.titulo,
    categoria: item.categoria,
    selo: item.selo,
    arquivo: item.arquivo?.[0],
    capa: item.capa?.[0],
    cor: item.corDaCapa,
  };
const valido = (doc) => doc && doc.titulo && doc.arquivo;

// Os PDFs são anexados na própria seção: `relatorio` é o do botão "Baixar
// relatório completo" e `materiais` é a lista dos outros, na ordem em que
// quem publica deixou no Squidex.
export const Documentos = ({ secao }) => {
  const [acervoAberto, setAcervoAberto] = useState(false);
  const abrirAcervo = useRef(null);

  const destaque = [lerPdf(secao.relatorio, 'relatorio')].find(valido);
  const outros = (secao.materiais || []).map((m, i) => lerPdf(m, `material-${i}`)).filter(valido);
  // Acervo ("Ver mais"): o relatório completo primeiro, depois a lista.
  const documentos = destaque ? [destaque, ...outros] : outros;
  const visiveis = outros.slice(0, VISIVEIS);
  const temMais = outros.length > VISIVEIS;
  // O cartão do acervo aparece sempre, como no protótipo: é o caminho para a
  // lista completa (inclusive o relatório em destaque), mesmo com poucos itens.
  const temAcervo = documentos.length > 0;

  const fecharAcervo = React.useCallback(() => {
    setAcervoAberto(false);
    abrirAcervo.current?.focus();
  }, []);

  return (
    <S.Documentos id="conteudos">
      <S.Container>
        <Cabecalho chapeu={secao.chapeu} titulo={secao.titulo} descricao={secao.descricao} />

        {destaque && (
          <S.RelatorioDestaque id="relatorio">
            <div>
              <h3>{destaque.titulo}</h3>
              {secao.relatorioResumo && <S.Paragrafo style={{ textAlign: 'left' }}>{secao.relatorioResumo}</S.Paragrafo>}
              {secao.relatorioBotao && (
                <S.Botao $variante="escuro" {...linkDoArquivo(destaque)}>
                  {secao.relatorioBotao}
                </S.Botao>
              )}
            </div>
            <div>
              <Capa doc={destaque} grande />
            </div>
          </S.RelatorioDestaque>
        )}

        {(visiveis.length > 0 || temAcervo) && (
          <S.Materiais>
            <div>
              {secao.outrosTitulo && <S.Rotulo>{secao.outrosTitulo}</S.Rotulo>}
              {temMais && secao.outrosResumo && (
                <S.Rotulo as="span" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                  {preencher(secao.outrosResumo, { n: VISIVEIS })}
                </S.Rotulo>
              )}
            </div>
            <div>
              {visiveis.map((doc) => (
                <S.Material key={doc.id} {...linkDoArquivo(doc)}>
                  <Capa doc={doc} />
                  <span>
                    <b>{doc.titulo}</b>
                  </span>
                </S.Material>
              ))}
              {temAcervo && (
                <S.Material as="button" type="button" ref={abrirAcervo} onClick={() => setAcervoAberto(true)}>
                  <S.VerMais>
                    <span>
                      <ArrowRight size={22} aria-hidden="true" />
                    </span>
                    {secao.verMaisRotulo}
                  </S.VerMais>
                  <span>
                    {secao.acervoCartao && <b>{secao.acervoCartao}</b>}
                    {secao.acervoContagem && <small>{preencher(secao.acervoContagem, { n: documentos.length })}</small>}
                  </span>
                </S.Material>
              )}
            </div>
          </S.Materiais>
        )}
      </S.Container>

      {acervoAberto && <Acervo documentos={documentos} aoFechar={fecharAcervo} secao={secao} />}
    </S.Documentos>
  );
};
